import React, { useState, useEffect } from "react";
import "../styles/userProfile.css";
import { useAuth } from "../context/AuthContext";
import {
  addVideoPost,
  getUserVideos,
  deleteVideo,
  getUserSettings,
} from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineDelete,
  AiOutlineVideoCamera,
  AiOutlineEnvironment,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";

const AREAS = ["Tiruchengode", "Sankagiri", "Kumarapalayam", "Pallipalayam"];

// 🔹 Upload to Cloudinary (Video)
async function uploadToCloudinary(file) {
  if (!file) return "";
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", uploadPreset);
  fd.append("resource_type", "video");

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
    {
      method: "POST",
      body: fd,
    }
  );
  const data = await res.json();
  return {
  secure_url: data.secure_url,
  public_id: data.public_id
};
}

export default function MyVideos() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userArea, setUserArea] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);

  const [caption, setCaption] = useState("");
  const [video, setVideo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pendingPost, setPendingPost] = useState(false);

  // ✅ Load user settings & videos
  const load = async () => {
    const list = await getUserVideos(user.uid);
    setVideos(list);
  };

  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      try {
        const s = await getUserSettings(user.uid);
        if (s) {
          setUserArea(s.area || "");
          setProfileName(s.name || user.displayName || "User");
          setProfilePhoto(s.photoURL || user.photoURL || null);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setProfileName(user.displayName || "User");
        setProfilePhoto(user.photoURL || null);
      }
      await load();
    })();
  }, [user]);

  // ✅ Create Video Post
  const handlePost = async () => {
    if (!video) {
      alert("Please select a video!");
      return;
    }

    const areaToUse = selectedArea || userArea;

    if (!areaToUse) {
      setPendingPost(true);
      setShowLocationPicker(true);
      return;
    }

    await actuallyPost(areaToUse);
  };

  // ✅ Upload and Post logic
  const actuallyPost = async (areaToUse) => {
    try {
      const up = await uploadToCloudinary(video);
await addVideoPost(
  user.uid,
  caption,
  up.secure_url,
  areaToUse,
  profileName,
  profilePhoto,
  up.public_id   // ✅ THIS IS CRITICAL
);
      setCaption("");
      setVideo(null);
      setPreview(null);
      setSelectedArea("");
      setShowLocationPicker(false);
      setPendingPost(false);
      await load();
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed");
    }
  };

  // ✅ Delete video
  const handleDelete = async (videoId) => {
    if (!window.confirm("Delete this video permanently?")) return;
    await deleteVideo(videoId);
    await load();
  };

  // ✅ Preview selected video
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    setVideo(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const removePreview = () => {
    setVideo(null);
    setPreview(null);
  };

  // ✅ Handle location confirmation
  const confirmLocation = async () => {
    if (!selectedArea) {
      alert("Please select a location before posting.");
      return;
    }

    setShowLocationPicker(false);
    if (pendingPost) {
      await actuallyPost(selectedArea);
    }
  };

  return (
    <div className="profile-page">
      {/* ===== Header ===== */}
      <button className="back-btn" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <h2 style={{ fontSize: "22px", marginBottom: "18px", fontWeight: "700" }}>
        My Videos
      </h2>

      {/* ===== Composer (same as Posts but for videos) ===== */}
      <div className="composer">
        {profilePhoto ? (
          <img
            src={profilePhoto}
            alt="User"
            className="composer-avatar"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <FaUserCircle className="composer-avatar-icon" />
        )}

        <input
          className="composer-input"
          placeholder="Write a caption..."
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />

        <label className="composer-attach">
          <input hidden type="file" accept="video/*" onChange={handleVideoChange} />
          <AiOutlineVideoCamera size={20} />
        </label>

        <div
          className="composer-location"
          onClick={() => setShowLocationPicker(true)}
          style={{ cursor: "pointer" }}
        >
          <AiOutlineEnvironment size={20} />
        </div>

        <button className="composer-post" onClick={handlePost}>
          Post
        </button>
      </div>

      {/* ✅ Video Preview */}
      {preview && (
        <div className="image-preview-box">
          <video src={preview} controls className="image-preview" />
          <button className="remove-preview-btn" onClick={removePreview}>
            <AiOutlineCloseCircle size={22} />
          </button>
        </div>
      )}

      {/* 📍 Location Picker Modal */}
      {showLocationPicker && (
        <div className="location-modal-overlay">
          <div className="location-modal">
            <h4>Select Location</h4>
            {AREAS.map((a) => (
              <label key={a}>
                <input
                  type="radio"
                  value={a}
                  checked={selectedArea === a}
                  onChange={(e) => setSelectedArea(e.target.value)}
                />
                {a}
              </label>
            ))}
            <button className="location-confirm-btn" onClick={confirmLocation}>
              Confirm
            </button>
          </div>
        </div>
      )}

      {/* ===== User Videos ===== */}
      <div className="posts-list" style={{ marginTop: "25px" }}>
        {videos.length === 0 ? (
          <p style={{ color: "#6b7280", marginTop: "20px" }}>No videos yet.</p>
        ) : (
          videos.map((v) => (
            <div key={v.id} className="post-item">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "8px",
                }}
              >
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <FaUserCircle style={{ fontSize: "36px", color: "#ccc" }} />
                )}
                <span style={{ fontWeight: 600, color: "#222", fontSize: "15px" }}>
                  {profileName}
                </span>
              </div>

              {v.area && <span className="post-area">📍 {v.area}</span>}
              {v.caption && <p className="post-text">{v.caption}</p>}
              {v.videoUrl && (
                <video
                  src={v.videoUrl}
                  controls
                  className="post-img"
                  style={{ borderRadius: "10px" }}
                />
              )}

              <button
                className="delete-btn"
                onClick={() => handleDelete(v.id)}
                title="Delete Video"
              >
                <AiOutlineDelete size={18} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
